import React, { useEffect, useMemo } from "react";

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace("/", "");
      return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
      // handle already-embed or share links
      if (u.pathname.startsWith("/embed/")) return url;
    }
  } catch (e) {
    // ignore
  }
  return null;
}

const VideoPlayerOverlay = ({ isOpen, url, onClose, title = "Video" }) => {
  const embedUrl = useMemo(() => getYouTubeEmbedUrl(url), [url]);
  const isYouTube = Boolean(embedUrl);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose && onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !url) return null;

  const backdropStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  const modalStyle = {
    position: "relative",
    width: "min(90vw, 1000px)",
    maxWidth: "100%",
    maxHeight: "90vh",
    background: "#111",
    borderRadius: 8,
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
    overflow: "hidden",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    color: "#fff",
    background: "rgba(255,255,255,0.06)",
  };

  const closeBtnStyle = {
    background: "transparent",
    color: "#fff",
    border: "none",
    fontSize: 18,
    cursor: "pointer",
    padding: 8,
    lineHeight: 1,
  };

  const bodyStyle = {
    padding: 0,
  };

  const iframeWrapperStyle = {
    position: "relative",
    width: "100%",
    // Maintain 16:9 while respecting max height via container
    paddingTop: "56.25%",
    background: "#000",
  };

  const iframeStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: 0,
  };

  const videoStyle = {
    display: "block",
    width: "100%",
    height: "auto",
    maxHeight: "calc(90vh - 44px)", // header height approx
    background: "#000",
  };

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={{ fontWeight: 600 }}>{title}</div>
          <button style={closeBtnStyle} aria-label="Close video" onClick={onClose}>
            ✕
          </button>
        </div>
        <div style={bodyStyle}>
          {isYouTube ? (
            <div style={iframeWrapperStyle}>
              <iframe
                title="Video player"
                src={`${embedUrl}?autoplay=1&modestbranding=1&rel=0`}
                style={iframeStyle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          ) : (
            <video src={url} controls style={videoStyle} />
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerOverlay;
